
import { NutritionalValues } from "@/types/recipe";
import { AlertCircle } from "lucide-react";

interface NutritionalInfoProps {
  nutritionalValues: NutritionalValues;
  servings?: number;
  onEdit?: (values: NutritionalValues) => void;
  editable?: boolean;
  defaultServings?: number;
}

const REFERENCE_INTAKE = {
  calories: 2014,
  fat_g: 69.5,
  sugar_g: 76.7,
  sodium_mg: 5830,
  protein_g: 50.2,
  carbohydrates_g: 264.6,
  fiber_g: 30
};

const NutritionalInfo = ({ 
  nutritionalValues, 
  servings = 1, 
  onEdit, 
  editable = false,
  defaultServings = 4 
}: NutritionalInfoProps) => {
  if (!nutritionalValues || Object.values(nutritionalValues).every(value => value === 0)) {
    return null;
  }

  const getValue = (value: number) => {
    if (editable) {
      return Number(value.toFixed(1));
    }
    const scaleFactor = servings / defaultServings;
    const scaledValue = value * scaleFactor;
    return Number(scaledValue.toFixed(1));
  };

  const calculatePercentage = (value: number, referenceValue: number, perServing: boolean = false) => {
    if (perServing) {
      const valuePerServing = value / defaultServings;
      return Number(((valuePerServing / referenceValue) * 100).toFixed(1));
    }
    return Number(((value / referenceValue) * 100).toFixed(1));
  };

  const getPercentageStyles = (percentage: number) => {
    if (percentage <= 10) return "bg-gray-50 text-gray-500";
    if (percentage <= 100) return "bg-emerald-50 text-emerald-700";
    return "bg-red-50 text-red-700";
  };

  const handleValueChange = (key: keyof NutritionalValues, value: string) => {
    if (!onEdit) return;
    const numValue = parseFloat(value) || 0;
    onEdit({
      ...nutritionalValues,
      [key]: numValue
    });
  };

  const renderPercentages = (value: number, referenceValue: number) => {
    const totalPercentage = calculatePercentage(getValue(value), referenceValue);
    const perServingPercentage = calculatePercentage(value, referenceValue, true);

    return (
      <div className="grid grid-cols-[1fr_1fr] gap-1 items-center justify-end w-full">
        <div className="flex justify-end">
          <span className={`px-2 py-0.5 rounded-full text-sm ${getPercentageStyles(totalPercentage)}`}>
            {totalPercentage}%
          </span>
        </div>
        <span className="text-xs text-gray-500 text-right">
          ({perServingPercentage}%)
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-lg font-medium">Nutritional Information</h2>
        
        {!editable && (
          <div className="grid grid-cols-[2fr_1fr_2fr] sm:grid-cols-[1fr_100px_200px] gap-2 text-sm text-gray-500 pb-2">
            <div>Nutrient</div>
            <div className="text-right">Amount</div>
            <div className="grid grid-cols-2 text-right">
              <div>Total %</div>
              <div>Per Serving</div>
            </div>
          </div>
        )}
        
        <div className="divide-y">
          {/* Calories row */}
          <div className={`grid ${editable ? 'grid-cols-[1fr_160px]' : 'grid-cols-[2fr_1fr_2fr] sm:grid-cols-[1fr_100px_200px]'} gap-2 py-2 items-center`}>
            <span className="font-medium">Calories</span>
            {editable ? (
              <div className="flex items-center gap-1 justify-end">
                <input
                  type="number"
                  value={nutritionalValues.calories}
                  onChange={(e) => handleValueChange('calories', e.target.value)}
                  className="w-24 text-right px-2 py-1 border rounded"
                />
                <span className="w-6"></span>
              </div>
            ) : (
              <span className="text-right tabular-nums">{getValue(nutritionalValues.calories)}</span>
            )}
            {!editable && renderPercentages(nutritionalValues.calories, REFERENCE_INTAKE.calories)}
          </div>

          {/* Protein row */}
          <div className={`grid ${editable ? 'grid-cols-[1fr_160px]' : 'grid-cols-[2fr_1fr_2fr] sm:grid-cols-[1fr_100px_200px]'} gap-2 py-2 items-center`}>
            <span className="font-medium">Protein</span>
            {editable ? (
              <div className="flex items-center gap-1 justify-end">
                <input
                  type="number"
                  value={nutritionalValues.protein_g}
                  onChange={(e) => handleValueChange('protein_g', e.target.value)}
                  className="w-24 text-right px-2 py-1 border rounded"
                />
                <span className="w-6">g</span>
              </div>
            ) : (
              <span className="text-right tabular-nums">{getValue(nutritionalValues.protein_g)}g</span>
            )}
            {!editable && renderPercentages(nutritionalValues.protein_g, REFERENCE_INTAKE.protein_g)}
          </div>

          {/* Carbohydrates row */}
          <div className={`grid ${editable ? 'grid-cols-[1fr_160px]' : 'grid-cols-[2fr_1fr_2fr] sm:grid-cols-[1fr_100px_200px]'} gap-2 py-2 items-center`}>
            <span className="font-medium">Carbs</span>
            {editable ? (
              <div className="flex items-center gap-1 justify-end">
                <input
                  type="number"
                  value={nutritionalValues.carbohydrates_g}
                  onChange={(e) => handleValueChange('carbohydrates_g', e.target.value)}
                  className="w-24 text-right px-2 py-1 border rounded"
                />
                <span className="w-6">g</span>
              </div>
            ) : (
              <span className="text-right tabular-nums">{getValue(nutritionalValues.carbohydrates_g)}g</span>
            )}
            {!editable && renderPercentages(nutritionalValues.carbohydrates_g, REFERENCE_INTAKE.carbohydrates_g)}
          </div>

          {/* Fat row */}
          <div className={`grid ${editable ? 'grid-cols-[1fr_160px]' : 'grid-cols-[2fr_1fr_2fr] sm:grid-cols-[1fr_100px_200px]'} gap-2 py-2 items-center`}>
            <span className="font-medium">Fat</span>
            {editable ? (
              <div className="flex items-center gap-1 justify-end">
                <input
                  type="number"
                  value={nutritionalValues.fat_g}
                  onChange={(e) => handleValueChange('fat_g', e.target.value)}
                  className="w-24 text-right px-2 py-1 border rounded"
                />
                <span className="w-6">g</span>
              </div>
            ) : (
              <span className="text-right tabular-nums">{getValue(nutritionalValues.fat_g)}g</span>
            )}
            {!editable && renderPercentages(nutritionalValues.fat_g, REFERENCE_INTAKE.fat_g)}
          </div>

          {/* Fiber row */}
          <div className={`grid ${editable ? 'grid-cols-[1fr_160px]' : 'grid-cols-[2fr_1fr_2fr] sm:grid-cols-[1fr_100px_200px]'} gap-2 py-2 items-center`}>
            <span className="font-medium">Fiber</span>
            {editable ? (
              <div className="flex items-center gap-1 justify-end">
                <input
                  type="number"
                  value={nutritionalValues.fiber_g}
                  onChange={(e) => handleValueChange('fiber_g', e.target.value)}
                  className="w-24 text-right px-2 py-1 border rounded"
                />
                <span className="w-6">g</span>
              </div>
            ) : (
              <span className="text-right tabular-nums">{getValue(nutritionalValues.fiber_g)}g</span>
            )}
            {!editable && renderPercentages(nutritionalValues.fiber_g, REFERENCE_INTAKE.fiber_g)}
          </div>

          {/* Sugar row */}
          <div className={`grid ${editable ? 'grid-cols-[1fr_160px]' : 'grid-cols-[2fr_1fr_2fr] sm:grid-cols-[1fr_100px_200px]'} gap-2 py-2 items-center`}>
            <span className="font-medium">Sugar</span>
            {editable ? (
              <div className="flex items-center gap-1 justify-end">
                <input
                  type="number"
                  value={nutritionalValues.sugar_g}
                  onChange={(e) => handleValueChange('sugar_g', e.target.value)}
                  className="w-24 text-right px-2 py-1 border rounded"
                />
                <span className="w-6">g</span>
              </div>
            ) : (
              <span className="text-right tabular-nums">{getValue(nutritionalValues.sugar_g)}g</span>
            )}
            {!editable && renderPercentages(nutritionalValues.sugar_g, REFERENCE_INTAKE.sugar_g)}
          </div>

          {/* Sodium row */}
          <div className={`grid ${editable ? 'grid-cols-[1fr_160px]' : 'grid-cols-[2fr_1fr_2fr] sm:grid-cols-[1fr_100px_200px]'} gap-2 py-2 items-center`}>
            <span className="font-medium">Sodium</span>
            {editable ? (
              <div className="flex items-center gap-1 justify-end">
                <input
                  type="number"
                  value={nutritionalValues.sodium_mg}
                  onChange={(e) => handleValueChange('sodium_mg', e.target.value)}
                  className="w-24 text-right px-2 py-1 border rounded"
                />
                <span className="w-6">mg</span>
              </div>
            ) : (
              <span className="text-right tabular-nums">{getValue(nutritionalValues.sodium_mg)}mg</span>
            )}
            {!editable && renderPercentages(nutritionalValues.sodium_mg, REFERENCE_INTAKE.sodium_mg)}
          </div>
        </div>
      </div>
      
      {!editable && (
        <div className="space-y-2 pt-4 border-t">
          <div className="flex items-center gap-2 p-2 bg-yellow-50 text-yellow-800 rounded-md">
            <AlertCircle className="w-4 h-4" />
            <p className="text-sm">Nutritional values are average estimates and may vary from actual values.</p>
          </div>
          <p className="text-sm text-gray-600 italic">
            * Percentages show total values for selected servings, with per-serving values in parentheses
          </p>
          <p className="text-sm text-gray-600 italic">
            * Based on an adult's daily reference intake
          </p>
        </div>
      )}
    </div>
  );
};

export default NutritionalInfo;
